// Vendors
import { TimerEnumeration } from '@gruposantander/opbpay-ui-core-models';
// Constants
import {
  HALF_SESSION_WARNED_CUSTOM_ACTION,
  SESSION_ALMOST_WARNED_CUSTOM_ACTION,
  SESSION_LIMIT_TIME_REACHED_CUSTOM_ACTION,
  SESSION_EXPIRE_NO_INIT_CUSTOM_ACTION,
  SESSION_EXPIRE_INIT_CUSTOM_ACTION,
  SESSION_EXPIRE_REFERENCE_REMOVED_CUSTOM_ACTION,
} from './constants/session-expire-log.handlers.constants';
import { ONBOARDING_INIT_LOCAL_STORAGE } from 'constants/app.constants';
// Enumerations
import { AppErrorEnumeration } from 'enumerations/app-error.enumeration';
// Event Handlers
import {
  fiveMinuteToGoOnOnboarding,
  halfOnboardingMaxTimeAchieved,
  onboardingInitDateStoredItemIsOn,
  onboardingTimeHasBeenReached,
  thereIsOnboardingInitDateAndFeatureEnabled,
} from './utils/session-expire.handlers.utils';
// Translations
import {
  SESSION_ALMOST_TIMEOUT,
  SESSION_HALF_TIMEOUT,
} from '../translations/session-expire.translations';
// Types
import {
  SessionExpireHandlersBuilderType,
  SessionExpireHandlersReturnType,
} from './types/session-expire.handlers.type';
// Utilities
import { expireCurrentOrderResolver } from 'resolvers/expire-order.resolver';

let HALF_WARNING_SHOWED = false,
  FIVE_MIN_WARNING_SHOWED = false;

const cleanUpSessionAndExpireOrder = async ({
  handleSendCustomActionEvent,
  setError,
}: Pick<
  SessionExpireHandlersBuilderType,
  'handleSendCustomActionEvent' | 'setError'
>): Promise<void> => {
  sessionStorage.removeItem(ONBOARDING_INIT_LOCAL_STORAGE);
  await expireCurrentOrderResolver();
  await handleSendCustomActionEvent(SESSION_LIMIT_TIME_REACHED_CUSTOM_ACTION);
  setError(AppErrorEnumeration.ONBOARDING_TIME_LIMIT_REACHED);
};

const closeWarningSnackbarEventHandler = ({
  setSessionWarningMessage,
}: Pick<SessionExpireHandlersBuilderType, 'setSessionWarningMessage'>): void => {
  setSessionWarningMessage('');
};

const closeAutomaticallyWarningSnackbar = ({
  setSessionWarningMessage,
}: Pick<SessionExpireHandlersBuilderType, 'setSessionWarningMessage'>): void => {
  setTimeout(() => {
    closeWarningSnackbarEventHandler({ setSessionWarningMessage });
  }, TimerEnumeration.TIMEOUT_THREE_SECONDS);
};

const initSessionExpiringObservationEvent = async ({
  formatMessage,
  handleSendCustomActionEvent,
  setError,
  setSessionWarningMessage,
}: SessionExpireHandlersBuilderType): Promise<void> => {
  switch (true) {
    case !onboardingInitDateStoredItemIsOn():
      await handleSendCustomActionEvent(SESSION_EXPIRE_REFERENCE_REMOVED_CUSTOM_ACTION);
      break;
    case onboardingTimeHasBeenReached():
      await cleanUpSessionAndExpireOrder({ handleSendCustomActionEvent, setError });
      break;
    case halfOnboardingMaxTimeAchieved(HALF_WARNING_SHOWED):
      HALF_WARNING_SHOWED = true;
      setSessionWarningMessage(formatMessage({ id: SESSION_HALF_TIMEOUT }));
      await handleSendCustomActionEvent(HALF_SESSION_WARNED_CUSTOM_ACTION);
      closeAutomaticallyWarningSnackbar({ setSessionWarningMessage });
      break;
    case fiveMinuteToGoOnOnboarding(FIVE_MIN_WARNING_SHOWED):
      FIVE_MIN_WARNING_SHOWED = true;
      setSessionWarningMessage(formatMessage({ id: SESSION_ALMOST_TIMEOUT }));
      handleSendCustomActionEvent(SESSION_ALMOST_WARNED_CUSTOM_ACTION);
      closeAutomaticallyWarningSnackbar({ setSessionWarningMessage });
      break;
    default:
      launchSessionExpireProcessTimeout({
        handleSendCustomActionEvent,
        formatMessage,
        setError,
        setSessionWarningMessage,
      });
      break;
  }
};

const launchSessionExpireProcessTimeout = props => {
  setTimeout(() => {
    initSessionExpiringObservationEvent(props);
  }, TimerEnumeration.TIMEOUT_ONE_SECOND);
};

const initSessionExpireObserveEventHandler = async ({
  handleSendCustomActionEvent,
  isUsableFeature,
  ...props
}: SessionExpireHandlersBuilderType): Promise<void> => {
  if (thereIsOnboardingInitDateAndFeatureEnabled(isUsableFeature)) {
    await handleSendCustomActionEvent(SESSION_EXPIRE_INIT_CUSTOM_ACTION);
    launchSessionExpireProcessTimeout({ handleSendCustomActionEvent, ...props });
  } else {
    await handleSendCustomActionEvent(SESSION_EXPIRE_NO_INIT_CUSTOM_ACTION);
  }
};

const SessionExpireHandlers = ({
  setSessionWarningMessage,
  ...props
}: SessionExpireHandlersBuilderType): SessionExpireHandlersReturnType => ({
  handleCloseWarningSnackbarEvent: () =>
    closeWarningSnackbarEventHandler({ setSessionWarningMessage }),
  handleInitSessionExpireObserveEvent: () =>
    initSessionExpireObserveEventHandler({ setSessionWarningMessage, ...props }),
});

export { SessionExpireHandlers };
